// shared-components/services/PreviewAnalyticsService.ts

import { supabase } from "@shared/core/services/SupabaseService";

interface PreviewPlayData {
  duration?: number;
  completed?: boolean;
  userAgent?: string;
  referrer?: string;
}

interface PreviewAnalyticsFilters {
  startDate?: string;
  endDate?: string;
  productId?: string;
  aggregateBy?: "hour" | "day" | "week" | "month";
}

interface AnalyticsSummary {
  totalPlays: number;
  totalCompletePlays: number;
  totalDuration: number;
  totalConversions: number;
  uniqueProducts: number;
  conversionRate: number;
  completionRate: number;
  averagePlayDuration: number;
  deviceBreakdown: Record<string, number>;
  browserBreakdown: Record<string, number>;
  topReferrers: Array<{ referrer: string; count: number }>;
}

interface ProductAnalytics {
  product_id: string;
  product_name: string;
  plays: number;
  complete_plays: number;
  duration: number;
  conversions: number;
}

interface DateAnalytics {
  date: string;
  plays: number;
  complete_plays: number;
  duration: number;
  conversions: number;
}

interface RawAnalyticsRecord {
  id?: string;
  product_id?: string;
  session_id?: string;
  duration?: number;
  completed?: boolean;
  created_at?: string;
  products?: {
    name: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface AggregatedAnalytics {
  summary: AnalyticsSummary;
  byProduct: ProductAnalytics[];
  byDate: DateAnalytics[];
  rawData: RawAnalyticsRecord[];
}

interface ConversionFunnel {
  previews: number;
  completions: number;
  conversions: number;
  completionRate: number;
  conversionRate: number;
}

class PreviewAnalyticsService {
  private tableName: string;
  private websiteId: string | null;
  private sessionId: string;

  constructor() {
    this.tableName = "music_preview_analytics";
    this.websiteId = null;
    this.sessionId = this.generateSessionId();
  }

  setWebsiteId(websiteId: string): void {
    this.websiteId = websiteId;
  }

  getWebsiteId(): string {
    if (!this.websiteId) {
      this.websiteId = import.meta.env.VITE_WEBSITE_ID;
    }

    if (!this.websiteId) {
      throw new Error("Website ID is not set for PreviewAnalyticsService.");
    }

    return this.websiteId;
  }

  /**
   * Generate unique session ID for tracking
   */
  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current session ID for conversion tracking
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Track preview play event
   */
  async trackPreviewPlay(
    productId: string,
    playData: PreviewPlayData = {},
  ): Promise<boolean> {
    const {
      duration = 0,
      completed = false,
      userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "",
      referrer = typeof document !== "undefined" ? document.referrer : "",
    } = playData;

    try {
      // Use the dedicated SQL function for proper deduplication
      const { data: _data, error } = await supabase.rpc(
        "track_preview_play_deduplicated",
        {
          p_product_id: productId,
          p_website_id: this.getWebsiteId(),
          p_session_id: this.sessionId,
          p_duration: Math.round(duration),
          p_completed: completed,
          p_user_agent: userAgent,
          p_referrer: referrer,
        },
      );

      if (error) {
        console.error("Failed to track preview play:", error);
        // Don't throw error to avoid breaking user experience
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error tracking preview play:", error);
      return false;
    }
  }

  /**
   * Track conversion event (preview -> purchase)
   */
  async trackConversion(productId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from(this.tableName)
        .update({
          conversion_count: 1,
          updated_at: new Date().toISOString(),
        })
        .eq("product_id", productId)
        .eq("website_id", this.getWebsiteId())
        .eq("session_id", this.sessionId)
        .eq("date_played", new Date().toISOString().split("T")[0]);

      if (error) {
        console.error("Failed to track conversion:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error tracking conversion:", error);
      return false;
    }
  }

  /**
   * Get preview analytics for admin dashboard
   */
  async getPreviewAnalytics(
    filters: PreviewAnalyticsFilters = {},
  ): Promise<AggregatedAnalytics> {
    const { startDate, endDate, productId, aggregateBy = "day" } = filters;

    let query = supabase
      .from(this.tableName)
      .select(
        `
        product_id,
        play_count,
        complete_plays,
        total_duration,
        conversion_count,
        date_played,
        products!inner (
          name,
          digital_metadata
        )
      `,
      )
      .eq("website_id", this.getWebsiteId());

    if (startDate) {
      query = query.gte("date_played", startDate);
    }

    if (endDate) {
      query = query.lte("date_played", endDate);
    }

    if (productId) {
      query = query.eq("product_id", productId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return this.aggregateAnalytics(data || [], aggregateBy);
  }

  /**
   * Aggregate analytics data
   */
  private aggregateAnalytics(
    data: RawAnalyticsRecord[],
    aggregateBy: "hour" | "day" | "week" | "month",
  ): AggregatedAnalytics {
    const summary: AnalyticsSummary = {
      totalPlays: 0,
      totalCompletePlays: 0,
      totalDuration: 0,
      totalConversions: 0,
      uniqueProducts: new Set<string>().size,
      conversionRate: 0,
      completionRate: 0,
      averagePlayDuration: 0,
      deviceBreakdown: {},
      browserBreakdown: {},
      topReferrers: [],
    };

    const byProduct: Record<string, ProductAnalytics> = {};
    const byDate: Record<string, DateAnalytics> = {};
    const uniqueProducts = new Set<string>();
    const deviceCounts: Record<string, number> = {};
    const browserCounts: Record<string, number> = {};
    const referrerCounts: Record<string, number> = {};

    data.forEach((record) => {
      // Summary stats
      summary.totalPlays += record.play_count;
      summary.totalCompletePlays += record.complete_plays;
      summary.totalDuration += record.total_duration;
      summary.totalConversions += record.conversion_count || 0;
      uniqueProducts.add(record.product_id);

      // Device and browser analytics
      const device = this.parseDeviceType(record.user_agent);
      const browser = this.parseBrowser(record.user_agent);
      const referrer = record.referrer || "Direct";

      deviceCounts[device] = (deviceCounts[device] || 0) + record.play_count;
      browserCounts[browser] =
        (browserCounts[browser] || 0) + record.play_count;
      referrerCounts[referrer] =
        (referrerCounts[referrer] || 0) + record.play_count;

      // By product
      if (!byProduct[record.product_id]) {
        byProduct[record.product_id] = {
          product_id: record.product_id,
          product_name: record.products?.name || "Unknown",
          plays: 0,
          complete_plays: 0,
          duration: 0,
          conversions: 0,
        };
      }
      byProduct[record.product_id].plays += record.play_count;
      byProduct[record.product_id].complete_plays += record.complete_plays;
      byProduct[record.product_id].duration += record.total_duration;
      byProduct[record.product_id].conversions += record.conversion_count || 0;

      // By date
      const dateKey = this.getDateKey(record.date_played, aggregateBy);
      if (!byDate[dateKey]) {
        byDate[dateKey] = {
          date: dateKey,
          plays: 0,
          complete_plays: 0,
          duration: 0,
          conversions: 0,
        };
      }
      byDate[dateKey].plays += record.play_count;
      byDate[dateKey].complete_plays += record.complete_plays;
      byDate[dateKey].duration += record.total_duration;
      byDate[dateKey].conversions += record.conversion_count || 0;
    });

    // Calculate rates
    summary.uniqueProducts = uniqueProducts.size;
    summary.conversionRate =
      summary.totalPlays > 0
        ? (summary.totalConversions / summary.totalPlays) * 100
        : 0;
    summary.completionRate =
      summary.totalPlays > 0
        ? (summary.totalCompletePlays / summary.totalPlays) * 100
        : 0;
    summary.averagePlayDuration =
      summary.totalPlays > 0 ? summary.totalDuration / summary.totalPlays : 0;

    // Set device/browser breakdown
    summary.deviceBreakdown = deviceCounts;
    summary.browserBreakdown = browserCounts;
    summary.topReferrers = Object.entries(referrerCounts)
      .map(([referrer, count]) => ({ referrer, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Sort and limit results
    const topProducts = Object.values(byProduct)
      .sort((a, b) => b.plays - a.plays)
      .slice(0, 10);

    const dateData = Object.values(byDate).sort((a, b) =>
      a.date.localeCompare(b.date),
    );

    return {
      summary,
      byProduct: topProducts,
      byDate: dateData,
      rawData: data,
    };
  }

  /**
   * Get date key for aggregation
   */
  private getDateKey(
    dateString: string,
    aggregateBy: "hour" | "day" | "week" | "month",
  ): string {
    const date = new Date(dateString);

    switch (aggregateBy) {
      case "hour":
        return `${date.toISOString().slice(0, 13)}:00:00Z`;
      case "day":
        return date.toISOString().split("T")[0];
      case "week": {
        const week = this.getWeekNumber(date);
        return `${date.getFullYear()}-W${week}`;
      }
      case "month":
        return `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}`;
      default:
        return date.toISOString().split("T")[0];
    }
  }

  /**
   * Get week number of year
   */
  private getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear =
      (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  /**
   * Get conversion funnel data
   */
  async getConversionFunnel(dateRange: number = 7): Promise<ConversionFunnel> {
    const startDate = new Date(Date.now() - dateRange * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

    const { data, error } = await supabase
      .from(this.tableName)
      .select("play_count, complete_plays, conversion_count")
      .eq("website_id", this.getWebsiteId())
      .gte("date_played", startDate);

    if (error) throw error;

    const funnel = (data || []).reduce(
      (acc, record) => {
        acc.previews += record.play_count;
        acc.completions += record.complete_plays;
        acc.conversions += record.conversion_count || 0;
        return acc;
      },
      {
        previews: 0,
        completions: 0,
        conversions: 0,
        completionRate: 0,
        conversionRate: 0,
      },
    );

    funnel.completionRate =
      funnel.previews > 0 ? (funnel.completions / funnel.previews) * 100 : 0;
    funnel.conversionRate =
      funnel.previews > 0 ? (funnel.conversions / funnel.previews) * 100 : 0;

    return funnel as ConversionFunnel;
  }

  /**
   * Get top performing products by conversion rate
   */
  async getTopPerformingProducts(
    limit: number = 10,
  ): Promise<ProductAnalytics[]> {
    const { data, error } = await supabase
      .from(this.tableName)
      .select(
        `
        product_id,
        play_count,
        complete_plays,
        total_duration,
        conversion_count,
        products!inner (
          name
        )
      `,
      )
      .eq("website_id", this.getWebsiteId())
      .gte(
        "date_played",
        new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
      );

    if (error) throw error;

    // Aggregate by product
    const productStats: Record<string, ProductAnalytics> = {};

    (data || []).forEach((record) => {
      if (!productStats[record.product_id]) {
        productStats[record.product_id] = {
          product_id: record.product_id,
          product_name: record.products?.name || "Unknown",
          plays: 0,
          complete_plays: 0,
          duration: 0,
          conversions: 0,
        };
      }
      productStats[record.product_id].plays += record.play_count;
      productStats[record.product_id].complete_plays += record.complete_plays;
      productStats[record.product_id].duration += record.total_duration;
      productStats[record.product_id].conversions +=
        record.conversion_count || 0;
    });

    // Calculate conversion rates and sort
    return Object.values(productStats)
      .map((product) => ({
        ...product,
        conversionRate:
          product.plays > 0 ? (product.conversions / product.plays) * 100 : 0,
      }))
      .sort((a, b) => (b.conversionRate || 0) - (a.conversionRate || 0))
      .slice(0, limit);
  }

  /**
   * Get real-time analytics for today
   */
  async getTodayAnalytics(): Promise<AggregatedAnalytics> {
    const today = new Date().toISOString().split("T")[0];
    return this.getPreviewAnalytics({
      startDate: today,
      endDate: today,
      aggregateBy: "hour",
    });
  }

  /**
   * Clear old analytics data (retention policy)
   */
  async clearOldAnalytics(daysToKeep: number = 90): Promise<number> {
    const cutoffDate = new Date(Date.now() - daysToKeep * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

    const { data, error } = await supabase
      .from(this.tableName)
      .delete()
      .lt("date_played", cutoffDate)
      .eq("website_id", this.getWebsiteId())
      .select();

    if (error) throw error;
    return data?.length || 0;
  }

  /**
   * Parse user agent to extract device type
   */
  private parseDeviceType(userAgent: string): string {
    if (!userAgent) return "Unknown";

    const ua = userAgent.toLowerCase();

    // Mobile devices
    if (ua.includes("mobile") || ua.includes("android")) return "Mobile";
    if (ua.includes("iphone") || ua.includes("ipod")) return "Mobile";

    // Tablets
    if (ua.includes("tablet") || ua.includes("ipad")) return "Tablet";

    // Desktop
    if (ua.includes("macintosh") || ua.includes("mac os")) return "Desktop";
    if (ua.includes("windows") || ua.includes("win32") || ua.includes("win64"))
      return "Desktop";
    if (ua.includes("linux") || ua.includes("x11")) return "Desktop";

    return "Other";
  }

  /**
   * Parse user agent to extract browser
   */
  private parseBrowser(userAgent: string): string {
    if (!userAgent) return "Unknown";

    const ua = userAgent.toLowerCase();

    // Order matters - check more specific browsers first
    if (ua.includes("edg/")) return "Edge";
    if (ua.includes("chrome/")) return "Chrome";
    if (ua.includes("firefox/")) return "Firefox";
    if (ua.includes("safari/") && !ua.includes("chrome")) return "Safari";
    if (ua.includes("opera/") || ua.includes("opr/")) return "Opera";

    return "Other";
  }

  /**
   * Get device and browser analytics
   */
  async getDeviceBrowserAnalytics(
    filters: PreviewAnalyticsFilters = {},
  ): Promise<{
    deviceBreakdown: Record<string, number>;
    browserBreakdown: Record<string, number>;
    topReferrers: Array<{ referrer: string; count: number }>;
  }> {
    const { startDate, endDate, productId } = filters;

    let query = supabase
      .from(this.tableName)
      .select("user_agent, referrer, play_count")
      .eq("website_id", this.getWebsiteId());

    if (startDate) query = query.gte("date_played", startDate);
    if (endDate) query = query.lte("date_played", endDate);
    if (productId) query = query.eq("product_id", productId);

    const { data, error } = await query;
    if (error) throw error;

    const deviceBreakdown: Record<string, number> = {};
    const browserBreakdown: Record<string, number> = {};
    const referrerCounts: Record<string, number> = {};

    (data || []).forEach((record) => {
      const device = this.parseDeviceType(record.user_agent);
      const browser = this.parseBrowser(record.user_agent);
      const referrer = record.referrer || "Direct";

      deviceBreakdown[device] =
        (deviceBreakdown[device] || 0) + record.play_count;
      browserBreakdown[browser] =
        (browserBreakdown[browser] || 0) + record.play_count;
      referrerCounts[referrer] =
        (referrerCounts[referrer] || 0) + record.play_count;
    });

    const topReferrers = Object.entries(referrerCounts)
      .map(([referrer, count]) => ({ referrer, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      deviceBreakdown,
      browserBreakdown,
      topReferrers,
    };
  }
}

export const previewAnalyticsService = new PreviewAnalyticsService();
